<template>
  <div
    :class="$options.name"
    :data-e-ga-id="uniqueId"
    :style="{ width: onlyIcon ? '40px' : inputWidth, height: 'fit-content' }"
  >
    <div :class="labelClass">
      <span v-if="label" class="tw:body-1">{{ label }}</span>
      <!--  @slot This slot is for the tooltip component.  -->
      <slot name="tooltip" />
    </div>
    <EdroneTooltip
      :close-trigger-class-name="closeTriggerClassName"
      :disabled="disabled"
      :offset="[0, 8]"
      custom-theme="dropdown"
      max-width="none"
      position="bottom-start"
      type="custom"
      @close="$emit('close')"
      @open="
        $emit('open');
        gtagUniversalSend({ component_id: uniqueId });
      "
    >
      <button
        :class="[
          { [`${$options.name}__button`]: true },
          { [`${$options.name}__button--disabled`]: disabled },
          { [`${$options.name}__button--invalid`]: invalid },
          { [`${$options.name}__button--border`]: showBorder },
          { [`${$options.name}__button--only-icon`]: onlyIcon },
          { [`${$options.name}__button--blue`]: variant === 'blue' },
          { [`${$options.name}__button--solid`]: variant === 'solid' },
          { [`${$options.name}__button--custom`]: variant === 'custom' },
          iconClass,
        ]"
        class="tw:px-4 tw:py-2 tw:max-h-10 tw:h-min tw:rounded-sm"
      >
        <font-awesome-icon v-if="onlyIcon" :icon="icon" />
        <template v-else>
          <div class="tw:flex tw:gap-3 tw:items-center">
            <font-awesome-icon v-if="icon" :icon="icon" style="width: 16px; height: 16px" />
            <span class="tw:body-1">{{ textDropdown }}</span>
          </div>
          <div :class="['tw:flex tw:gap-2 tw:items-center', badge || showArrow ? 'tw:ms-3' : '']">
            <EdroneBadge v-if="badge" :text="badge" :type="badgeType" size="small" solid />
            <font-awesome-icon v-if="showArrow" icon="fa-light fa-caret-down" />
          </div>
        </template>
      </button>

      <template #custom>
        <EdroneDropdownContent :style="{ width: itemsWidth, 'max-height': maxContentHeight, overflow: 'auto' }">
          <template v-if="items">
            <template v-for="(item, index) in items">
              <EdroneDropdownItem
                v-if="item.type === 'item'"
                :key="index"
                :active="item.active"
                :close-trigger-class-name="item.closeTriggerClassName"
                :desc="item.desc"
                :icon="item.icon"
                :text="item.text"
                :unique-id="uniqueId + '-' + index"
                @click="item.onClick && item.onClick()"
              />
              <EdroneDropdownHeader
                v-if="item.type === 'header'"
                :key="index"
                :icon="item.icon"
                :text="item.text"
                :unique-id="uniqueId + '-' + index"
              />
              <EdroneDropdownSeparator v-if="item.type === 'separator'" :key="index" />
            </template>
          </template>
          <template v-else>
            <slot />
          </template>
        </EdroneDropdownContent>
      </template>
    </EdroneTooltip>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import EdroneDropdownContent from "./EdroneDropdownContent.vue";
import EdroneDropdownItem from "./EdroneDropdownItem.vue";
import EdroneDropdownHeader from "./EdroneDropdownHeader.vue";
import EdroneDropdownSeparator from "./EdroneDropdownSeparator.vue";
import EdroneTooltip from "./EdroneTooltip.vue";
import EdroneBadge from "./EdroneBadgeStatus.vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";
import { BADGE_STATUS_TYPE } from "edrone-components/EdroneBadgeStatus.constants.ts";

export type EdroneDropdownItems = {
  type: "item" | "separator" | "header";
  icon?: string;
  text: string;
  desc?: string;
  active?: boolean;
  closeTriggerClassName: string;
  onClick?: () => void;
};

export default defineComponent({
  name: "EdroneDropdown",
  components: {
    EdroneDropdownItem,
    EdroneDropdownHeader,
    EdroneDropdownContent,
    EdroneDropdownSeparator,
    EdroneTooltip,
    EdroneBadge,
  },
  props: {
    /**
     * Text to display in the dropdown.
     */
    textDropdown: {
      type: String,
      required: false,
      default: "Select value",
    },
    /**
     * Badge to display in the dropdown.
     */
    badge: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Badge Type to display in the dropdown.
     */
    badgeType: {
      type: String as PropType<
        BADGE_STATUS_TYPE | "default" | "success" | "error" | "warn" | "info" | "violet" | "orange" | "purple"
      >,
      required: false,
      default: BADGE_STATUS_TYPE.DEFAULT,
    },
    /**
     * Label to display on the dropdown.
     */
    label: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Width of the button dropdown.
     */
    inputWidth: {
      type: String,
      required: false,
      default: "340px",
    },
    /**
     * Width of the content dropdown.
     */
    itemsWidth: {
      type: String,
      required: false,
      default: "340px",
    },
    /**
     * Max height of the content dropdown.
     */
    maxContentHeight: {
      type: String,
      required: false,
      default: "max-content",
    },
    /**
     * If true, the dropdown will have a border.
     */
    showBorder: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * If true, the dropdown will have an arrow icon on the right side.
     */
    showArrow: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Icon to display in the dropdown.
     */
    icon: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * If true, the dropdown will only display the icon.
     */
    onlyIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Array of items to display in the dropdown.
     */
    items: {
      type: Array as () => EdroneDropdownItems[],
      required: false,
      default: null,
    },
    /**
     * If true, the dropdown will be in invalid state.
     */
    invalid: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If true, the dropdown will be disabled.
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Class name that trigger closing dropdown after clicking element that has this class name. When this prop is not provided, then clicking anywhere inside the dropdown will close it.  Default: "close_dropdown"
     */
    closeTriggerClassName: {
      type: String,
      required: false,
      default: "close_dropdown",
    },
    /**
     * Value to set proper component styles
     */
    variant: {
      type: String as PropType<"custom" | "blue" | "solid">,
      required: false,
      default: "custom",
      validator: (value: string) => ["custom", "blue", "solid"].includes(value),
    },
    uniqueId: {
      type: String,
      required: true,
    },
  },
  emits: {
    // this function is triggered when the tooltip is closed
    close: () => true,
    // this function is triggered when the tooltip is opened
    open: () => true,
  },
  setup(props, { slots }) {
    const iconClass = props.onlyIcon ? "tw:justify-center" : "tw:justify-between";
    const labelClass = props.label || slots.tooltip ? "tw:flex tw:gap-1 tw:mb-1 tw:items-center" : undefined;

    return { iconClass, labelClass };
  },
  methods: { gtagUniversalSend },
});
</script>

<style>
@reference "vue-app/assets/scss/main.css";

/* not used scoped because we override tooltip styles */
.EdroneDropdown svg {
  transition: transform 0.3s ease-in-out;
}

.EdroneDropdown svg.open {
  transform: rotate(180deg);
}

.EdroneDropdown__button {
  display: flex;
  align-items: center;
  min-width: fit-content;
  width: 100%;
  font-size: pxToRem(16);
  transition: background-color 0.3s ease-in-out;
  z-index: 1;
  @apply tw:bg-white;
}

.EdroneDropdown__button--custom {
  @apply tw:text-black;
}

.EdroneDropdown__button--custom.EdroneDropdown__button--border {
  @apply tw:border tw:border-default-200;
}

.EdroneDropdown__button--custom.EdroneDropdown__button--border:hover:not(.EdroneDropdown__button--disabled) {
  @apply tw:border-default-400;
}

.EdroneDropdown__button--custom:hover,
.EdroneDropdown__button--custom:focus {
  @apply tw:bg-default-000;
}

.EdroneDropdown__button--custom.EdroneDropdown__button--disabled {
  border-color: transparent;
  cursor: not-allowed;
  @apply tw:text-default-700;
}

.EdroneDropdown__button--custom.EdroneDropdown__button--disabled svg {
  @apply tw:text-default-500;
}

.EdroneDropdown__button--custom.EdroneDropdown__button--disabled.EdroneDropdown__button--border {
  @apply tw:bg-default-100;
}

.EdroneDropdown__button--blue {
  @apply tw:text-blue-500;
}

.EdroneDropdown__button--blue.EdroneDropdown__button--border {
  @apply tw:border tw:border-blue-200;
}

.EdroneDropdown__button--blue.EdroneDropdown__button--border:hover:not(.EdroneDropdown__button--disabled) {
  @apply tw:border-blue-400;
}

.EdroneDropdown__button--blue:focus,
.EdroneDropdown__button--blue:hover {
  @apply tw:bg-blue-000 tw:text-blue-600;
}

.EdroneDropdown__button--blue.EdroneDropdown__button--disabled {
  border-color: transparent;
  cursor: not-allowed;
  @apply tw:bg-white tw:text-blue-200;
}

.EdroneDropdown__button--solid {
  @apply tw:text-black tw:bg-branding-500;
}

.EdroneDropdown__button--solid.EdroneDropdown__button--border {
  @apply tw:border tw:border-black tw:outline tw:outline-1 tw:outline-transparent;
}

.EdroneDropdown__button--solid.EdroneDropdown__button--border:hover:not(.EdroneDropdown__button--disabled) {
  @apply tw:outline-black tw:outline tw:outline-1;
}

.EdroneDropdown__button--solid.EdroneDropdown__button--disabled {
  cursor: not-allowed;
  @apply tw:bg-branding-000 tw:border-default-200 tw:text-default-500 tw:outline-hidden;
}

.EdroneDropdown__button--only-icon {
  min-width: 40px;
  padding: 12px;
  height: 40px;
  width: 40px;
  text-align: center;
}

.EdroneDropdown__button--invalid {
  @apply tw:!border tw:!border-red-500;
}

.EdroneDropdown .EdroneTooltip > div,
.EdroneDropdown > .EdroneTooltip {
  width: 100%;
}

.tippy-box[data-theme~="dropdown"] {
  margin: 0;
  padding: 0;
  background-color: transparent;
  box-shadow: none;
}

.tippy-box[data-theme~="dropdown"] .tippy-content {
  margin: 0;
  padding: 0;
}
</style>
